import './coapplicantEditResidencePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/AddressLineOne/AddressLineOne';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import { housingTypeOptions } from '../../../store/applicantStore/constants/housingTypeOptions';
import { monthsAtResidenceOptions } from '../../../store/applicantStore/constants/monthsAtResidenceOptions';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: { coapplicantReviewEditPathname, coapplicantReviewEditProgress },
    coapplicantCurrentResidenceCity,
    coapplicantCurrentResidenceLineOne,
    coapplicantCurrentResidenceLineTwo,
    coapplicantCurrentResidenceMonthlyPaymentAmount,
    coapplicantCurrentResidenceMonthsAt,
    coapplicantCurrentResidencePostalCode,
    coapplicantCurrentResidenceState,
    coapplicantCurrentResidenceType,
    coapplicantPreviousResidenceCity,
    coapplicantPreviousResidenceLineOne,
    coapplicantPreviousResidenceLineTwo,
    coapplicantPreviousResidencePostalCode,
    coapplicantPreviousResidenceState,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceCity,
    setCoapplicantCurrentResidenceLineOne,
    setCoapplicantCurrentResidenceLineTwo,
    setCoapplicantCurrentResidenceMonthlyPaymentAmount,
    setCoapplicantCurrentResidenceMonthsAt,
    setCoapplicantCurrentResidencePostalCode,
    setCoapplicantCurrentResidenceState,
    setCoapplicantCurrentResidenceType,
    setCoapplicantPreviousResidenceCity,
    setCoapplicantPreviousResidenceLineOne,
    setCoapplicantPreviousResidenceLineTwo,
    setCoapplicantPreviousResidencePostalCode,
    setCoapplicantPreviousResidenceState,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      currentCityValue: coapplicantCurrentResidenceCity,
      currentLineOneValue: coapplicantCurrentResidenceLineOne,
      currentLineTwoValue: coapplicantCurrentResidenceLineTwo,
      currentMonthsAtResidenceValue: coapplicantCurrentResidenceMonthsAt,
      currentPostalCodeValue: coapplicantCurrentResidencePostalCode,
      currentStateValue: coapplicantCurrentResidenceState,
      housingAmountValue: toCurrencyString(
        coapplicantCurrentResidenceMonthlyPaymentAmount,
      ),
      housingTypeValue: coapplicantCurrentResidenceType,
      previousCityValue: coapplicantPreviousResidenceCity,
      previousLineOneValue: coapplicantPreviousResidenceLineOne,
      previousLineTwoValue: coapplicantPreviousResidenceLineTwo,
      previousPostalCodeValue: coapplicantPreviousResidencePostalCode,
      previousStateValue: coapplicantPreviousResidenceState,
    },
    delayError: 200,
    mode: 'onTouched',
    shouldFocusError: false,
  });

  const shouldShowPreviousResidenceFields =
    watch('currentMonthsAtResidenceValue') === ONE_YEAR_IN_MONTHS_AS_STRING;

  const housingTypeValidation = {
    required: t('validation.housingType.required'),
  };

  const housingAmountValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const monthsAtResidenceValidation = {
    required: t('validation.housingType.required'),
  };

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      currentCityValue,
      currentLineOneValue,
      currentLineTwoValue,
      currentMonthsAtResidenceValue,
      currentPostalCodeValue,
      currentStateValue,
      housingAmountValue,
      housingTypeValue,
      previousCityValue,
      previousLineOneValue,
      previousLineTwoValue,
      previousPostalCodeValue,
      previousStateValue,
    } = values;

    if (
      !currentCityValue ||
      !currentLineOneValue ||
      !currentMonthsAtResidenceValue ||
      !currentPostalCodeValue ||
      !currentStateValue ||
      !housingAmountValue ||
      !housingTypeValue
    ) {
      return;
    }

    if (
      shouldShowPreviousResidenceFields &&
      (!previousLineOneValue ||
        !previousCityValue ||
        !previousStateValue ||
        !previousPostalCodeValue)
    ) {
      return;
    }

    setCoapplicantCurrentResidenceCity(currentCityValue);
    setCoapplicantCurrentResidenceLineOne(currentLineOneValue);
    setCoapplicantCurrentResidenceLineTwo(currentLineTwoValue);
    setCoapplicantCurrentResidenceMonthlyPaymentAmount(housingAmountValue);
    setCoapplicantCurrentResidenceMonthsAt(currentMonthsAtResidenceValue);
    setCoapplicantCurrentResidencePostalCode(currentPostalCodeValue);
    setCoapplicantCurrentResidenceState(currentStateValue);
    setCoapplicantCurrentResidenceType(housingTypeValue);

    if (shouldShowPreviousResidenceFields) {
      setCoapplicantPreviousResidenceCity(previousCityValue);
      setCoapplicantPreviousResidenceLineOne(previousLineOneValue);
      setCoapplicantPreviousResidenceLineTwo(previousLineTwoValue);
      setCoapplicantPreviousResidencePostalCode(previousPostalCodeValue);
      setCoapplicantPreviousResidenceState(previousStateValue);
    } else {
      setCoapplicantPreviousResidenceCity(null);
      setCoapplicantPreviousResidenceLineOne(null);
      setCoapplicantPreviousResidenceLineTwo(null);
      setCoapplicantPreviousResidencePostalCode(null);
      setCoapplicantPreviousResidenceState(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-edit-residence-page" sx={theme.pageLayout}>
        <Header
          progress={coapplicantReviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantEditResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditResidence.form.h1}>
                {t('CoapplicantEditResidencePage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditResidence.form.h2}>
                {t('CoapplicantEditResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register('housingTypeValue', housingTypeValidation)}
                    error={errors.housingTypeValue}
                    errorTestId={
                      locator.pages.coapplicantEditResidence.form
                        .residenceTypeSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditResidence.form
                        .residenceTypeSelect
                    }
                    label={t('CoapplicantEditResidencePage.housingType')}
                    options={housingTypeOptions}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register('housingAmountValue', housingAmountValidation)}
                    data-testid={
                      locator.pages.coapplicantEditResidence.form.amountInput
                    }
                    error={errors.housingAmountValue}
                    errorTestId={
                      locator.pages.coapplicantEditResidence.form
                        .amountInputError
                    }
                    label={t('CoapplicantEditResidencePage.housingAmount')}
                    currency
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    data-testid={
                      locator.pages.coapplicantEditResidence.form
                        .addressLine1Input
                    }
                    errorTestId={
                      locator.pages.coapplicantEditResidence.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    label={t('CoapplicantEditResidencePage.residenceLineOne')}
                    name="currentLineOneValue"
                    register={register}
                    setValue={setValue}
                    suggestionFields={{
                      cityFieldName: 'currentCityValue',
                      stateFieldName: 'currentStateValue',
                      postalCodeFieldName: 'currentPostalCodeValue',
                    }}
                    validation={addressValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Input
                        {...register('currentLineTwoValue')}
                        autoComplete="address-line2"
                        data-testid={
                          locator.pages.coapplicantEditResidence.form
                            .addressLine2Input
                        }
                        label={t(
                          'CoapplicantEditResidencePage.residenceLineTwo',
                        )}
                      />
                    </div>

                    <div className="input-half">
                      <Input
                        {...register('currentCityValue', cityValidation)}
                        autoComplete="address-level2"
                        data-testid={
                          locator.pages.coapplicantEditResidence.form.cityInput
                        }
                        error={errors.currentCityValue}
                        errorTestId={
                          locator.pages.coapplicantEditResidence.form
                            .cityInputError
                        }
                        label={t('CoapplicantEditResidencePage.residenceCity')}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Select
                        {...register('currentStateValue', stateValidation)}
                        error={errors.currentStateValue}
                        errorTestId={
                          locator.pages.coapplicantEditResidence.form
                            .stateSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditResidence.form
                            .stateSelect
                        }
                        label={t('CoapplicantEditResidencePage.residenceState')}
                        options={USStates.map(({ abbreviation, name }) => ({
                          name,
                          value: abbreviation,
                        }))}
                      />
                    </div>

                    <div className="input-half">
                      <NumberInput
                        {...register(
                          'currentPostalCodeValue',
                          postalCodeValidation,
                        )}
                        autoComplete="postal-code"
                        data-testid={
                          locator.pages.coapplicantEditResidence.form
                            .zipCodeInput
                        }
                        error={errors.currentPostalCodeValue}
                        errorTestId={
                          locator.pages.coapplicantEditResidence.form
                            .zipCodeInputError
                        }
                        label={t('CoapplicantEditResidencePage.zip')}
                        type="number"
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register(
                      'currentMonthsAtResidenceValue',
                      monthsAtResidenceValidation,
                    )}
                    error={errors.currentMonthsAtResidenceValue}
                    errorTestId={
                      locator.pages.coapplicantEditResidence.form
                        .monthSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditResidence.form.monthSelect
                    }
                    label={t('CoapplicantEditResidencePage.duration')}
                    options={monthsAtResidenceOptions}
                  />
                </div>
              </div>

              {shouldShowPreviousResidenceFields && (
                <div
                  data-testid="previous-residence-section"
                  style={{ marginBottom: '3rem' }}
                >
                  <h3>{t('CoapplicantEditResidencePage.previousResidence')}</h3>

                  <div style={{ marginTop: '1.5rem' }}>
                    <AddressLineOne
                      data-testid={
                        locator.pages.coapplicantEditResidence.form
                          .previousAddressLine1Input
                      }
                      errorTestId={
                        locator.pages.coapplicantEditResidence.form
                          .previousAddressLine1InputError
                      }
                      errors={errors}
                      label={t('CoapplicantEditResidencePage.residenceLineOne')}
                      name="previousLineOneValue"
                      register={register}
                      setValue={setValue}
                      suggestionFields={{
                        cityFieldName: 'previousCityValue',
                        stateFieldName: 'previousStateValue',
                        postalCodeFieldName: 'previousPostalCodeValue',
                      }}
                      validation={addressValidation}
                    />
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <Input
                          {...register('previousLineTwoValue')}
                          autoComplete="address-line2"
                          data-testid={
                            locator.pages.coapplicantEditResidence.form
                              .previousAddressLine2Input
                          }
                          label={t(
                            'CoapplicantEditResidencePage.residenceLineTwo',
                          )}
                        />
                      </div>

                      <div className="input-half">
                        <Input
                          {...register('previousCityValue', cityValidation)}
                          autoComplete="address-level2"
                          data-testid={
                            locator.pages.coapplicantEditResidence.form
                              .previousCityInput
                          }
                          error={errors.previousCityValue}
                          errorTestId={
                            locator.pages.coapplicantEditResidence.form
                              .previousCityInputError
                          }
                          label={t(
                            'CoapplicantEditResidencePage.residenceCity',
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <Select
                          {...register('previousStateValue', stateValidation)}
                          error={errors.previousStateValue}
                          errorTestId={
                            locator.pages.coapplicantEditResidence.form
                              .previousStateSelectError
                          }
                          inputTestId={
                            locator.pages.coapplicantEditResidence.form
                              .previousStateSelect
                          }
                          label={t(
                            'CoapplicantEditResidencePage.residenceState',
                          )}
                          options={USStates.map(({ abbreviation, name }) => ({
                            name,
                            value: abbreviation,
                          }))}
                        />
                      </div>

                      <div className="input-half">
                        <NumberInput
                          {...register(
                            'previousPostalCodeValue',
                            postalCodeValidation,
                          )}
                          autoComplete="postal-code"
                          data-testid={
                            locator.pages.coapplicantEditResidence.form
                              .previousZipCodeInput
                          }
                          error={errors.previousPostalCodeValue}
                          errorTestId={
                            locator.pages.coapplicantEditResidence.form
                              .previousZipCodeInputError
                          }
                          label={t('CoapplicantEditResidencePage.zip')}
                          type="number"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.coapplicantEditResidence.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditResidencePage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.coapplicantEditResidence.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isSyncingCoapplicant}
                    fullWidth
                    submit
                  >
                    {t('CoapplicantEditResidencePage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditResidence.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditResidencePage';
